import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin } from 'rxjs';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { CODE_CATALOG_URL } from '@app/pages/admin/developer-catalog-page/developer-catalog.utils';
import { isCodeCatalogEnabled } from '@app/utils/environment-features';
import { AuthService } from '@services/auth.service';
import { OrchestrationAccessService } from '@services/orchestration-access.service';

@Component({
  selector: 'app-orchestration-shell',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule],
  templateUrl: './orchestration-shell.html',
  styleUrl: './orchestration-shell.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrchestrationShellComponent {
  private readonly auth = inject(AuthService);
  private readonly access = inject(OrchestrationAccessService);
  private readonly destroyRef = inject(DestroyRef);
  readonly isAdmin = this.auth.isAdmin();
  readonly canOrchestrate = signal(this.isAdmin);
  readonly canManageAccess = signal(this.isAdmin);
  private readonly allLinks = [
    { label: 'Overview', icon: 'pi pi-home', url: '/admin/process-management' },
    { label: 'Processes', icon: 'pi pi-sitemap', url: '/admin/process-management/processes' },
    { label: 'adminNotifications.title', icon: 'pi pi-bell', url: '/admin/process-management/notifications' },
    { label: 'Assignments', icon: 'pi pi-list-check', url: '/admin/process-management/assignments' },
    { label: 'Operation Monitoring', icon: 'pi pi-chart-line', url: '/admin/process-management/monitoring' },
    { label: 'Workers', icon: 'pi pi-users', url: '/admin/process-management/workers' },
    { label: 'Storage', icon: 'pi pi-server', url: '/admin/process-management/storage' },
    { label: 'Satellite scenes', icon: 'pi pi-images', url: '/admin/process-management/satellite-products' },
    { label: 'ORCH_GIS_TITLE', icon: 'pi pi-map-marker', url: '/admin/process-management/gis' },
    { label: 'HOST_LAYOUT_TITLE', icon: 'pi pi-box', url: '/admin/process-management/host-layout' },
    { label: 'GEOSERVER_MONITOR_TITLE', icon: 'pi pi-map', url: '/admin/process-management/geoserver' },
    { label: 'Subscriptions', icon: 'pi pi-calendar', url: '/admin/process-management/subscriptions' },
    { label: 'Command configuration', icon: 'pi pi-cog', url: '/admin/process-management/command-config' },
    { label: 'Code catalog', icon: 'pi pi-code', url: CODE_CATALOG_URL },
    { label: 'External service calls', icon: 'pi pi-globe', url: '/admin/process-management/external-service-calls' },
    { label: 'API_USAGE_TITLE', icon: 'pi pi-chart-bar', url: '/admin/process-management/api-usage' },
    { label: 'SYSTEM_SETTINGS_TITLE', icon: 'pi pi-cog', url: '/admin/process-management/settings' },
    { label: 'Access', icon: 'pi pi-lock', url: '/admin/process-management/access' },
    { label: 'System logs', icon: 'pi pi-file', url: '/admin/logging' },
  ].filter(link => link.url !== CODE_CATALOG_URL || isCodeCatalogEnabled());

  readonly links = computed(() => this.allLinks.filter(link =>
    link.url === '/admin/process-management/access' ? this.canManageAccess()
      : link.url === '/admin/logging' ? this.isAdmin : this.canOrchestrate()));

  constructor() {
    if (!this.isAdmin) {
      forkJoin({ orchestration: this.access.hasOrchestrationAdmin(), access: this.access.hasAccessManage() })
        .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(capabilities => {
          this.canOrchestrate.set(capabilities.orchestration);
          this.canManageAccess.set(capabilities.access);
        });
    }
  }
}
